import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

const predicates = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['touches', 'ST_TOUCHES'],
  ['covers', 'ST_COVERS']
])

const predicatesDatabricks = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['touches', 'SEDONA_ST_TOUCHES'],
  ['covers', 'SEDONA_ST_COVERS']
])

class SpatialJoin extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialjoin',
        group: 'Joins',
        title: 'Spatial Join',
        description: `
    This component performs a spatial join between two tables, based on a spatial predicate.
    `,
        starred: true
      },
      [
        {
          name: 'maintable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'secondarytable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Secondary table'
        },
        {
          name: 'maincolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in main table',
          parent: 'maintable',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'secondarycolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in secondary table',
          parent: 'secondarytable',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'predicate',
          type: WorkflowComponentParamType.Selection,
          title: 'Spatial predicate',
          default: predicates.keys().next().value,
          optional: true,
          options: Array.from(predicates.keys())
        }
      ]
    )
    this.supportedProviders = [
      Provider.Redshift,
      Provider.Postgres,
      Provider.BigQuery,
      Provider.DatabricksRest
    ]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    let outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)

    let colString = ''
    const columns = schemas?.input?.['secondarytable'] as TableSchema
    if (columns) {
      colString = columns
        .map((x) => `_joined.${x.name} AS ${x.name}_joined`)
        .join(', ')
    } else {
      return {
        query: '',
        outputs: {}
      }
    }

    let select = ''
    if (connection.type === Provider.DatabricksRest) {
      let geomFromWKBFunct = 'ST_GEOMFROMWKB'
      if (params.predicate === 'touches' || params.predicate === 'covers') {
        geomFromWKBFunct = 'SEDONA_ST_GEOMFROMWKB'
      }
      const leftOutputTableName = connection.quoteName(
        outputTablename + '_left'
      )
      const rightOutputTableName = connection.quoteName(
        outputTablename + '_right'
      )

      select = `WITH _main AS (
        SELECT /*+ REPARTITION(40, ${params.maincolumn}) */
        *, ${geomFromWKBFunct}(${params.maincolumn}) AS ${
        params.maincolumn
      }_processed FROM ${leftOutputTableName}
      ),
      _joined AS (
        SELECT /*+ REPARTITION(40, ${params.secondarycolumn}) */
        *, ${geomFromWKBFunct}(${params.secondarycolumn}) AS ${
        params.secondarycolumn
      }_processed FROM ${rightOutputTableName}
      )
      SELECT /*+ REPARTITION(40) */
        _main.*,
        ${colString}
      FROM
        _main,
        _joined
      WHERE
          (_main.__carto_bounds_xmax >= _joined.__carto_bounds_xmin
          AND _main.__carto_bounds_xmin <= _joined.__carto_bounds_xmax
          AND _main.__carto_bounds_ymax >= _joined.__carto_bounds_ymin
          AND _main.__carto_bounds_ymin <= _joined.__carto_bounds_ymax)
        AND
          ${predicatesDatabricks.get(params.predicate as string)}(
            _main.${params.maincolumn}_processed,
            _joined.${params.secondarycolumn}_processed
          )`
    } else {
      select = `SELECT
                      _main.*,
                      ${colString}
                    FROM
                    ${connection.quoteName(
                      preparedParams.maintable as string
                    )} AS _main,
                    ${connection.quoteName(
                      preparedParams.secondarytable as string
                    )} AS _joined
                    WHERE ${predicates.get(params.predicate as string)}(
                      _main.${params.maincolumn},
                      _joined.${params.secondarycolumn}
                    )`
    }
    let createTable = connection.getCreateTempTableQuery(
      outputTablename,
      select,
      codeType,
      null
    )

    let query = ''
    if (connection.type === Provider.DatabricksRest) {
      const leftOutputTableName = connection.quoteName(
        outputTablename + '_left'
      )
      const rightOutputTableName = connection.quoteName(
        outputTablename + '_right'
      )
      const generateSpatialFilterTablesQuery = (
        connection as DBricksConnection
      ).generateSpatialFilterTables(
        connection.quoteName(preparedParams.maintable as string),
        connection.quoteName(preparedParams.secondarytable as string),
        preparedParams.maincolumn as string,
        preparedParams.secondarycolumn as string,
        leftOutputTableName,
        rightOutputTableName
      )
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        createTable
      )
      query = dedent`
        ${generateSpatialFilterTablesQuery}
        ${createTable}
      `
    } else {
      query = createTable
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    const schema = columns.maintable.map((column) => {
      return {
        name: `${column.name}`,
        type: column.type
      }
    })
    columns.secondarytable.forEach((column) => {
      schema.push({
        name: `${column.name}_joined`,
        type: column.type
      })
    })
    return schema
  }
}

export { SpatialJoin }
